import { useTranslation } from "@privacy/translations";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import {
  addCustomerLibraryProcessingToOrganisationMutationOptions,
  customerLibraryQueryOptions,
  type CustomerLibraryProcessing,
} from "../../../api/registrations";
import { Drawer } from "../../../shared/Drawer";
import { EmptyState } from "../../../shared/EmptyState";
import { multilineEllipsis } from "../../../shared/multilineEllipsis";
import { PAGE_PADDING_X } from "../../../shared/Page";
import { STATS_LIST_PADDING_X, StatsList } from "./StatsList";

export const CustomerLibraryPanel = () => {
  const { t } = useTranslation();
  const customerLibraryQuery = useQuery(customerLibraryQueryOptions());

  if (!customerLibraryQuery.data) return <Drawer.Loading />;

  const available = customerLibraryQuery.data.filter(processing => !processing.isAddedToOrganisation);
  const inRegister = customerLibraryQuery.data.filter(processing => processing.isAddedToOrganisation);

  return (
    <StatsList label={t("RegistrationGrid.SidePanels.CustomerLibrary.Title")}>
      {customerLibraryQuery.data.length === 0 && (
        <Box sx={{ height: "50vh" }}>
          <EmptyState labelKey="RegistrationGrid.SidePanels.CustomerLibrary.Empty" />
        </Box>
      )}
      {available.length > 0 && (
        <>
          <SectionLabel>{t("RegistrationGrid.SidePanels.CustomerLibrary.Available")}</SectionLabel>
          {available.map(processing => (
            <AvailableProcessingRow key={processing.id} processing={processing} />
          ))}
        </>
      )}
      {inRegister.length > 0 && (
        <>
          <SectionLabel>{t("RegistrationGrid.SidePanels.CustomerLibrary.InRegister")}</SectionLabel>
          {inRegister.map(processing => (
            <ProcessingRow key={processing.id} processing={processing} />
          ))}
        </>
      )}
    </StatsList>
  );
};

const AvailableProcessingRow = ({ processing }: { processing: CustomerLibraryProcessing }) => {
  const { t } = useTranslation();
  const addToOrganisationMutation = useMutation(addCustomerLibraryProcessingToOrganisationMutationOptions());

  return (
    <ProcessingRow processing={processing}>
      <Button
        variant="text"
        size="small"
        sx={{ flexShrink: 0, color: theme => theme.palette.primary["600"] }}
        loading={addToOrganisationMutation.isPending}
        onClick={() => addToOrganisationMutation.mutate(processing.id)}
      >
        {t("RegistrationGrid.SidePanels.CustomerLibrary.Use")}
      </Button>
    </ProcessingRow>
  );
};

const ProcessingRow = ({ processing, children }: { processing: CustomerLibraryProcessing; children?: ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 3,
      borderBottom: "1px solid",
      borderColor: "divider",
      py: 1,
      pl: STATS_LIST_PADDING_X,
      pr: PAGE_PADDING_X,
    }}
  >
    <Stack sx={{ minWidth: 0, maxWidth: "50%" }}>
      <Tooltip title={processing.name} placement="top-start">
        <Typography
          sx={{
            ...multilineEllipsis(2),
            fontWeight: 600,
            fontSize: theme => theme.typography.pxToRem(14),
          }}
        >
          {processing.name}
        </Typography>
      </Tooltip>
      {processing.purpose && (
        <Typography
          sx={{
            whiteSpace: "pre-line",
            overflowWrap: "anywhere",
            fontSize: theme => theme.typography.pxToRem(13),
            color: theme => theme.palette.text.secondary,
          }}
        >
          {processing.purpose}
        </Typography>
      )}
    </Stack>
    {children}
  </Box>
);

const SectionLabel = ({ children }: { children: ReactNode }) => (
  <Typography
    sx={{
      px: STATS_LIST_PADDING_X,
      pt: 3,
      pb: 1,
      borderBottom: 1,
      borderColor: "divider",
      fontWeight: 600,
      fontSize: theme => theme.typography.pxToRem(12),
      color: theme => theme.palette.text.secondary,
    }}
  >
    {children}
  </Typography>
);
